/**
 * 무대 목표 편집기(2026-10-08 · FS-15-08) 순수 도우미 — 잴 것(measure)마다 칸 · 칸 고치기 · 프리셋 바꾸기 · 다음 버전 · 검사.
 * 목표 모양은 libs/modes StageGoal(types.ts) · 스키마 stageGoal 과 같다(빈 칸은 지운다).
 */
import type { ModeVersion } from "../types";

export const MEASURES = ["turns", "swings", "stops", "hold", "notes", "reach", "beeps"] as const;
export type Measure = (typeof MEASURES)[number];

/** 숫자 칸 · 짝 칸(최소 · 최대) · 켜기 칸 · 음 이름 칸 */
export type GoalField =
  | "n"
  | "amp"
  | "pauseS"
  | "growing"
  | "rate"
  | "range"
  | "seconds"
  | "turns"
  | "tol"
  | "stopS"
  | "distinct"
  | "sequence";

/** 잴 것마다 보일 칸(앞이 주 칸) · 관절/포트를 쓰나 */
export const MEASURE_FIELDS: Record<Measure, { fields: readonly GoalField[]; target: boolean }> = {
  turns: { fields: ["n", "rate"], target: true },
  swings: { fields: ["n", "amp", "pauseS", "growing"], target: true },
  stops: { fields: ["n", "turns", "stopS", "tol"], target: true },
  hold: { fields: ["seconds", "rate", "range"], target: true },
  notes: { fields: ["distinct", "sequence"], target: false },
  reach: { fields: ["amp"], target: true },
  beeps: { fields: ["n", "range", "amp"], target: false },
};
export const PAIR_FIELDS: readonly GoalField[] = ["rate", "range"];
export const PORTS = ["A", "B", "C", "D", "E", "F"] as const;

export type Goal = Record<string, unknown> & { measure: Measure };

/** 칸 하나 바꾸기 — 값이 비면(undefined · '' · NaN · 빈 목록) 지운다 */
export function setGoalField(g: Goal, field: string, value: unknown): Goal {
  const out: Goal = { ...g };
  const empty =
    value === undefined ||
    value === null ||
    value === "" ||
    (typeof value === "number" && !Number.isFinite(value)) ||
    (Array.isArray(value) && value.length === 0) ||
    value === false;
  if (empty) delete out[field];
  else out[field] = value;
  return out;
}

/** 잴 것을 바꾸면 그 잴 것이 쓰지 않는 칸은 지운다(관절 · 포트 · 문구는 남긴다) */
export function changeMeasure(g: Goal, m: Measure): Goal {
  const keep = new Set<string>([
    "measure",
    "joint",
    "port",
    "label",
    "labelKey",
    ...MEASURE_FIELDS[m].fields,
  ]);
  const out: Goal = { measure: m };
  for (const [k, v] of Object.entries(g)) if (keep.has(k) && k !== "measure") out[k] = v;
  if (!MEASURE_FIELDS[m].target && m === "notes") {
    delete out.joint;
    delete out.port;
  }
  return out;
}

/** 관절 또는 포트(하나만) */
export function setTarget(g: Goal, kind: "joint" | "port" | "none", value: string): Goal {
  const out: Goal = { ...g };
  delete out.joint;
  delete out.port;
  if (kind !== "none" && value) out[kind] = value;
  return out;
}

type Def = {
  version: number;
  presets: { key: string; timerS: number; stage?: Record<string, unknown> }[];
} & Record<string, unknown>;

/** 프리셋 하나를 바꾼 새 정의(깊은 복사 없이 바뀐 갈래만 새로) */
export function patchPreset<T extends Def>(
  def: T,
  key: string,
  fn: (p: T["presets"][number]) => T["presets"][number],
): T {
  return { ...def, presets: def.presets.map((p) => (p.key === key ? fn(p) : p)) };
}

/**
 * 저장할 버전 — 마지막 버전이 배포 안 된 초안이면 그 번호(덮어쓴다), 아니면 가장 큰 번호 + 1.
 * @returns [버전, 초안을 덮어쓰나]
 */
export function draftVersionOf(versions: readonly ModeVersion[]): [number, boolean] {
  const last = [...versions].sort((a, b) => b.version - a.version)[0];
  if (!last) return [1, false];
  return last.publishedAt ? [last.version + 1, false] : [last.version, true];
}

/** 정의 전체 검사(libs/modes — 셸 · 커널과 같은 규칙 · ajv 는 늦게 불러온다) — 이 프리셋 경로의 것과 나머지를 나눠 돌려준다 */
export async function checkDefinition(def: unknown, presetIndex: number) {
  const { validateModeDefinition } = await import("@picnic-field/modes");
  const r = validateModeDefinition(def);
  const mine = `/presets/${presetIndex}`;
  const issues = r.ok ? [] : r.issues;
  return {
    ok: r.ok,
    preset: issues.filter((i) => i.path === mine || i.path.startsWith(`${mine}/`)),
    other: issues.filter((i) => !(i.path === mine || i.path.startsWith(`${mine}/`))),
  };
}

/** 두 정의에서 바뀐 프리셋 키 */
export function changedPresets(a: Def | null, b: Def | null): string[] {
  if (!a || !b) return [];
  const before = new Map(a.presets.map((p) => [p.key, JSON.stringify(p)]));
  return b.presets.filter((p) => before.get(p.key) !== JSON.stringify(p)).map((p) => p.key);
}
